import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useContactLog, useLatestContactLogDate, usePhotoViewUrls } from '../../hooks/api';
import { useSendMessage } from '../../hooks/api/useMessages';
import { useDateNavigation, useCalendarWeek, usePatient } from '../../hooks';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition/useSpeechRecognition';
import type { ReplyTo } from '../../lib/api';
import {
  RecordSection,
  CalendarSection,
  CommentSection,
  ImageCarousel,
  NextVisitSection,
} from './components';
import { DateHeader } from './components/';
import {
  EmptyState,
  PageDataBoundary,
  PatientSwitcher,
  ReplyBottomSheet,
  SendSuccessDialog,
} from '../../components';
import { getHomeDateInfo, isMessageSendingClosed } from '../../utils';
import { ContentEvents } from '../../lib/analytics';

const Container = styled.div`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.pageBackground};
  display: flex;
  flex-direction: column;
  position: relative;
  padding-bottom: 70px;
  max-width: 100vw;
  overflow-x: hidden;
`;

export const HomePage: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [replyMessageText, setReplyMessageText] = useState('');
  const [replyHideFromPatient, setReplyHideFromPatient] = useState(false);
  const [replyTo, setReplyTo] = useState<ReplyTo | null>(null);
  const [showSendSuccessDialog, setShowSendSuccessDialog] = useState(false);
  const commentSectionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const sendMessageMutation = useSendMessage();
  const {
    state: voiceState,
    lastSegment,
    segmentId,
    startRecording,
    stopRecording,
    resetRecording,
  } = useSpeechRecognition();
  const isRecording = voiceState === 'recording';
  const isProcessing = voiceState === 'processing';

  // PatientContext から患者選択状態を取得
  // ※ linkedPatients の取得完了は AppReadyGate で保証済みのため isLoading は受け取らない
  const {
    selectedPatientId,
    selectedPatientIndex,
    selectedPatient,
    linkedPatients,
    selectPatientByIndex,
  } = usePatient();

  // 患者切り替え時に写真インデックスと返信状態をリセット
  useEffect(() => {
    setCurrentImageIndex(0);
    setIsReplyOpen(false);
    setReplyMessageText('');
    setReplyHideFromPatient(false);
    setReplyTo(null);
    resetRecording();
  }, [selectedPatientId, resetRecording]);

  // 最新連絡帳日付を取得
  const { data: latestDateData, isError: isErrorLatestDate } = useLatestContactLogDate(selectedPatientId);

  // 最新日付を計算
  // - latestDateDataがundefined（ロード中）→ undefinedを渡す
  // - 取得失敗（retry後に isError=true / data=undefined）→ nullを渡し、useDateNavigationが今日を採用して進める
  //   （isPageReady が永続的に false となり無限スピナーになるのを防ぐ）
  // - has_data: true → latest_dateを渡す
  // - has_data: false → nullを渡す
  const latestDate = latestDateData === undefined
    ? (isErrorLatestDate ? null : undefined)
    : latestDateData.has_data
      ? latestDateData.latest_date
      : null;

  // 日付ナビゲーションフックを使用（患者IDを渡して切り替え検知）
  const {
    selectedDate,
    selectedDateString,
    changeDate,
    goToToday,
  } = useDateNavigation(latestDate, selectedPatientId);

  // 連絡帳データを取得（過去の写真も含める）
  // selectedDateStringがundefinedの場合は空文字列を渡す（useContactLog側でenabledを制御）
  const { data: contactLogData, isLoading: isLoadingContactLog } = useContactLog(
    selectedDateString ? selectedPatientId : null,
    selectedDateString ?? '',
    true
  );

  // Analytics: 連絡帳閲覧イベント（データ取得成功時）
  useEffect(() => {
    if (contactLogData && selectedDateString) {
      ContentEvents.viewContactLog(selectedDateString);
    }
  }, [contactLogData, selectedDateString]);

  // 既読計測ロジック: 3秒以上表示された場合に既読APIを呼び出す
  useEffect(() => {
    // content_idが存在しない場合は何もしない（トップレベルから取得）
    const contentId = contactLogData?.content_id;
    if (!contentId || !selectedPatientId || !selectedDateString) {
      console.log('[HomePage] 既読計測スキップ:', {
        contentId,
        selectedPatientId,
        selectedDateString,
        hasContactLogData: !!contactLogData,
      });
      return;
    }

    console.log('[HomePage] 既読計測開始:', {
      contentId,
      selectedPatientId,
      selectedDateString,
    });

    const viewStartTime = Date.now();
    let isVisible = true;

    // Page Visibility API でバックグラウンド時間を除外
    const handleVisibilityChange = () => {
      isVisible = !document.hidden;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // クリーンアップ関数: コンポーネントがアンマウントされるか、依存関係が変わったときに実行
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // ページが可視状態かつ3秒以上滞在した場合のみ既読APIを呼び出す
      if (isVisible) {
        const viewDuration = (Date.now() - viewStartTime) / 1000;
        console.log('[HomePage] 既読計測終了:', {
          contentId,
          viewDuration: viewDuration.toFixed(2) + '秒',
          threshold: '3秒',
          willMarkAsViewed: viewDuration >= 3,
        });

        if (viewDuration >= 3) {
          // 非同期で既読APIを呼び出す（エラーが発生してもユーザー体験に影響しない）
          import('../../lib/api').then(({ default: apiClient }) => {
            console.log('[HomePage] 既読API呼び出し中...');
            apiClient.markContentAsViewed({
              content_id: contentId,
              patient_id: selectedPatientId,
              date: selectedDateString,
              view_duration_seconds: Math.floor(viewDuration),
            }).then(response => {
              console.log('[HomePage] 既読API成功:', response);
            }).catch(error => {
              console.error('[HomePage] 既読API失敗:', error);
            });
          });
        }
      } else {
        console.log('[HomePage] 既読計測スキップ（バックグラウンド）');
      }
    };
  }, [contactLogData?.content_id, selectedPatientId, selectedDateString]);

  // カレンダー週のフックを使用
  const {
    weekDays,
    goToPrevWeek,
    goToNextWeek,
  } = useCalendarWeek(selectedDate, selectedPatientId);

  // 写真のS3キーを取得（当日の写真）
  const photoS3Keys = useMemo(() => {
    if (!contactLogData?.photos) return [];
    return contactLogData.photos.map(photo => photo.s3_key);
  }, [contactLogData?.photos]);

  // 過去の写真のS3キーを取得
  const pastPhotoS3Keys = useMemo(() => {
    if (!contactLogData?.past_photos) return [];
    return contactLogData.past_photos.map(photo => photo.s3_key);
  }, [contactLogData?.past_photos]);

  // 写真の閲覧用URLを取得
  const { data: photoViewUrls } = usePhotoViewUrls(photoS3Keys);
  const { data: pastPhotoViewUrls } = usePhotoViewUrls(pastPhotoS3Keys);

  // 画像リスト（当日の写真 → 過去の写真、s3_keyベースの重複排除付き）
  const images = useMemo(() => {
    const imageList: Array<{ url: string; date?: string; s3_key?: string }> = [];
    const seenKeys = new Set<string>();

    // 1. 当日の写真を追加（contactLogData.dateを使用）
    if (photoViewUrls && Array.isArray(photoViewUrls) && contactLogData) {
      photoViewUrls.forEach((photo) => {
        if (photo.view_url && !photo.error) {
          if (photo.s3_key) seenKeys.add(photo.s3_key);
          imageList.push({
            url: photo.view_url,
            date: contactLogData.date,  // 当日の日付を使用
            s3_key: photo.s3_key,
          });
        }
      });
    }

    // 2. 過去の写真を追加（最大30枚、新→古の順、s3_key重複排除）
    // DBから取得したphoto_dateを使用
    if (pastPhotoViewUrls && Array.isArray(pastPhotoViewUrls) && contactLogData?.past_photos) {
      pastPhotoViewUrls.forEach((photoUrl) => {
        if (photoUrl.view_url && !photoUrl.error && !seenKeys.has(photoUrl.s3_key)) {
          seenKeys.add(photoUrl.s3_key);
          // s3_keyで対応するPhotoDataを検索してphoto_dateを取得
          const photoData = contactLogData.past_photos?.find(p => p.s3_key === photoUrl.s3_key);
          imageList.push({
            url: photoUrl.view_url,
            date: photoData?.photo_date,  // DBから取得した日付を使用
            s3_key: photoUrl.s3_key,
          });
        }
      });
    }

    return imageList;
  }, [photoViewUrls, pastPhotoViewUrls, contactLogData]);

  const dateInfo = selectedDate ? getHomeDateInfo(selectedDate) : null;

  // サムネイルクリックハンドラー（useCallbackで最適化）
  const handleThumbnailClick = useCallback((imageIndex: number) => {
    setCurrentImageIndex(imageIndex);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // カレンダー日付クリックハンドラー（useCallbackで最適化）
  const handleDayClick = useCallback((date: Date) => {
    changeDate(date);
  }, [changeDate]);

  // 患者切り替えハンドラー
  const handlePatientChange = useCallback((index: number) => {
    selectPatientByIndex(index);
  }, [selectPatientByIndex]);

  // デイサービス利用中止日翌日以降は送信導線をトルツメ
  const isSendingClosed = isMessageSendingClosed(selectedPatient);

  // 送信閉鎖に切り替わった瞬間に ReplyBottomSheet 関連の親ステートをリセットする。
  // これを怠ると、後で利用中止日が解除されて送信導線が復活した際に
  // BottomSheet が意図せず開いた状態で再表示される可能性がある。
  useEffect(() => {
    if (!isSendingClosed) return;
    setIsReplyOpen(false);
    setReplyMessageText('');
    setReplyHideFromPatient(false);
    setReplyTo(null);
    resetRecording();
  }, [isSendingClosed, resetRecording]);

  // 音声入力テキストを返信メッセージに反映（差分のみ追記）
  useEffect(() => {
    if (lastSegment) {
      setReplyMessageText((prev) => prev + lastSegment);
    }
  }, [segmentId]);

  // 連絡帳コメントへの返信ハンドラー（BottomSheet表示）
  const handleReplyToComment = useCallback(() => {
    const newReplyTo: ReplyTo = {
      type: 'contact_comment',
      source_date: selectedDateString || '',
      source_content: contactLogData?.contact_message?.body || '',
      facility_name: selectedPatient?.office_name,
    };
    setReplyTo(newReplyTo);
    commentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setIsReplyOpen(true);
  }, [selectedDateString, contactLogData?.contact_message?.body, selectedPatient?.office_name]);

  const handleReplyClose = useCallback(() => {
    setIsReplyOpen(false);
    resetRecording();
  }, [resetRecording]);

  const handleReplyVoiceInput = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      resetRecording();
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording, resetRecording]);

  const handleReplySend = useCallback(() => {
    if (!selectedPatientId || !replyMessageText.trim()) return;

    sendMessageMutation.mutate(
      {
        patient_id: selectedPatientId,
        content: replyMessageText.trim(),
        reply_to: replyTo || undefined,
        hide_from_patient: replyHideFromPatient || undefined,
      },
      {
        onSuccess: () => {
          setIsReplyOpen(false);
          setReplyMessageText('');
          setReplyHideFromPatient(false);
          setReplyTo(null);
          resetRecording();
          setShowSendSuccessDialog(true);
        },
      }
    );
  }, [selectedPatientId, replyMessageText, replyTo, replyHideFromPatient, sendMessageMutation, resetRecording]);

  const handleSendSuccessConfirm = useCallback(() => {
    setShowSendSuccessDialog(false);
    const searchParams = selectedPatientId ? `?patient_id=${selectedPatientId}` : '';
    navigate(`/message${searchParams}`, { state: { initialMode: 'history' } });
  }, [navigate, selectedPatientId]);

  // 患者データがない場合
  if (!linkedPatients || linkedPatients.length === 0) {
    return (
      <EmptyState
        fullPage
        title="連携しているご家族が見つかりません"
        description="事業所と連携を行ってください"
        icon="👤"
      />
    );
  }

  // ページデータ準備完了判定:
  // - latestDateData: useLatestContactLogDate の結果（undefined = 未完了）
  //   ※ ただし isErrorLatestDate=true の場合は latestDate=null として扱い、selectedDate を今日に倒して進める
  // - selectedDate: latestDate に基づいて useDateNavigation が確定したか
  // - isLoadingContactLog: useContactLog の取得中フラグ
  // ※ すべて揃うまで本文を描画しない（部分描画によるチラつきを防止）
  const isPageReady =
    (latestDateData !== undefined || isErrorLatestDate) &&
    selectedDate !== undefined &&
    !isLoadingContactLog;

  return (
    <PageDataBoundary ready={isPageReady}>
      <Container>
        {/* 利用者切り替えタブ */}
        <PatientSwitcher
          patients={linkedPatients}
          selectedIndex={selectedPatientIndex}
          onPatientChange={handlePatientChange}
        />

        {/* 画像カルーセル */}
        {images.length > 0 && (
          <ImageCarousel
            images={images}
            currentIndex={currentImageIndex}
            onIndexChange={setCurrentImageIndex}
          />
        )}

        {/* 日付ヘッダー */}
        <DateHeader dateText={dateInfo?.full ?? ''} />

        {/* 連絡帳コメント */}
        {(contactLogData?.contact_message?.body ||
          (contactLogData?.photos && contactLogData.photos.length > 0)) && (
          <CommentSection
            ref={commentSectionRef}
            facilityName={selectedPatient?.office_name || '未設定'}
            commentMessage={contactLogData?.contact_message?.body}
            photos={contactLogData?.photos}
            photoViewUrls={photoViewUrls}
            images={images}
            onThumbnailClick={handleThumbnailClick}
            onReplyClick={
              !isSendingClosed && contactLogData?.contact_message?.body
                ? handleReplyToComment
                : undefined
            }
          />
        )}

        {/* バイタル・実施記録 */}
        {(contactLogData?.vitals ||
          (contactLogData?.meal &&
            !contactLogData.meal.is_not_provided) ||
          contactLogData?.hydration?.amount_ml ||
          contactLogData?.bathing?.is_recorded ||
          contactLogData?.training) ? (
          <RecordSection
            patientId={selectedPatientId}
            vitals={contactLogData?.vitals}
            meal={contactLogData?.meal}
            hydration={contactLogData?.hydration}
            bathing={contactLogData?.bathing}
            training={contactLogData?.training}
            selectedDate={selectedDate}
          />
        ) : null}

        {/* 来所日について（非表示設定・日付未入力の場合はタイトルごと消える） */}
        <NextVisitSection
          nextVisit={contactLogData?.next_visit}
          contactDate={selectedDateString}
        />

        {/* カレンダー */}
        <CalendarSection
          weekDays={weekDays}
          onPrevWeek={goToPrevWeek}
          onNextWeek={goToNextWeek}
          onDayClick={handleDayClick}
        />

        {/* 返信BottomSheet（デイサービス利用中止日翌日以降は非表示） */}
        {!isSendingClosed && (
          <ReplyBottomSheet
            isOpen={isReplyOpen}
            onClose={handleReplyClose}
            messageText={replyMessageText}
            onMessageTextChange={setReplyMessageText}
            hideFromPatient={replyHideFromPatient}
            onHideFromPatientChange={setReplyHideFromPatient}
            onVoiceInput={handleReplyVoiceInput}
            onSend={handleReplySend}
            isSending={sendMessageMutation.isPending}
            isRecording={isRecording}
            isProcessing={isProcessing}
            showHideFromPatient={selectedPatient?.relationship !== '本人'}
          />
        )}

        {/* 送信成功ダイアログ */}
        <SendSuccessDialog
          isOpen={showSendSuccessDialog}
          onClose={() => setShowSendSuccessDialog(false)}
          onConfirm={handleSendSuccessConfirm}
          officeName={selectedPatient?.office_name || ''}
        />
      </Container>
    </PageDataBoundary>
  );
};

export default HomePage;
